import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { acquireWeaponMaterials, buildAmmoBox, buildWeaponMesh, disposeWeaponMesh, releaseWeaponMaterials } from '../weaponMeshes';
import type { WeaponId } from '../weaponData';
import { ROOM_ORDER } from './stock';
import { interiorModel } from '../../render/models/interiorModels';
import { loadModelPack, releaseModelPack, type PackModel } from '../../render/models/modelLibrary';
import { buildInteriorShell, type InteriorShell, type ShellLayout } from '../../render/interiorShell';
import backroomLayout from '../../render/interiors/backroom.json';

/**
 * Sid's back room at Gorringe's (docs/WEAPONS.md §6): a compact 1970 interior drawn INSTEAD of the city
 * while the player is inside (render/sceneOverride.ts). The room is the backdrop (a bare bulb on a flex,
 * distempered walls over a green dado, shelves of boxed rounds and tins, a varnished counter with green
 * baize, the peg rack), kept dim and fogged so it falls away behind the **showcase**: the weapon you are
 * looking at hangs large over the counter, turning slowly under a warm key light with a cool rim light
 * behind it, a pool of light on the baize under it and dust drifting in the beam. Browsing slides one out
 * and the next in, a purchase drops it out of frame "into your coat" and brings it back. Built on the way
 * in (the screen is black), disposed on the way out; nothing is allocated per frame.
 *
 * Cost: the room is the baked shell (render/interiorShell.ts: lino, a nicotine paper over a distempered dado,
 * a Cycles lightmap and AO; four draws) and ONE merged vertex-coloured mesh for the rest (+ the bulb, the sign, the baize, the light pool),
 * six weapons of two or three draw calls each (only the one in the showcase and one sliding out are
 * visible), a few ammo boxes and one points cloud: about 15 draw calls, and the city draws none while
 * this is up. The key light casts the only shadow (one 1024 map, only where the tier has shadows). On MEDIUM and
 * HIGH the counter and the shelving are Meshy models (`backroom` pack, +2 draws, released on the way out).
 */

const FELT_TOP = 0.982;
/** Where the weapon in the showcase hangs (over the middle of the counter), and the eye that looks at it. */
const SHOW = new THREE.Vector3(-0.15, 1.3, 0.02);
const EYE = new THREE.Vector3(-0.15, 1.78, 1.92);
/** Half the box that has to fit on screen around SHOW (m): the hero, its swing, a little air. */
const FIT_HALF_W = 0.86;
const FIT_HALF_H = 0.5;
/** Length on screen of the longest weapon and of the shortest (m): a knife is shown big, but still smaller than a bat. */
const HERO_MAX = 0.9;
const HERO_MIN = 0.5;
/** How far a weapon slides sideways to leave the showcase (m), and the swing of the turntable (rad). */
const SLIDE = 1.5;
const SWING = 0.55;
/** Three-quarter view: the business end turns a little toward you. */
const BASE_YAW = -0.32;
/** The "into your coat" drop after a purchase (s). */
const POCKET_TIME = 1.35;

/**
 * Images of the hand's X, Y, Z axes in the showcase (weaponMeshes.ts: business end down -Y, top +Z):
 * business end to the right, top up, the broad side to the camera (guns, blades, bars: the hand's X;
 * the bat's face: Z).
 */
const BASIS: Record<'x' | 'z', [THREE.Vector3, THREE.Vector3, THREE.Vector3]> = {
  x: [new THREE.Vector3(0, 0, -1), new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, 1, 0)],
  z: [new THREE.Vector3(0, 1, 0), new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, 0, 1)],
};
const SIDE: Partial<Record<WeaponId, 'x' | 'z'>> = { bat: 'z' };

interface Item {
  id: WeaponId;
  pivot: THREE.Group;
  mesh: THREE.Group;
  /** Sideways offset from the showcase (m): 0 in it, ±SLIDE off stage. */
  x: number;
  /** Where it is heading: 0 when chosen, the side it left by otherwise. */
  wantX: number;
}

const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _box = new THREE.Box3();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);

/** Deterministic little noise for paint and plank variation. */
function hash(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

const smooth = (t: number): number => {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
};

/** Merged, vertex-coloured, non-indexed boxes and cylinders: the room is one draw call. */
export class RoomKit {
  private readonly parts: THREE.BufferGeometry[] = [];
  private n = 0;

  private push(g: THREE.BufferGeometry, color: number, vary: number): void {
    const geo = g.index ? g.toNonIndexed() : g;
    if (geo !== g) g.dispose();
    const c = new THREE.Color(color);
    const count = geo.getAttribute('position').count;
    const col = new Float32Array(count * 3);
    // One shade per face (six vertices), so plaster, planks and card read as uneven without a texture.
    for (let i = 0; i < count; i++) {
      const k = 1 + (hash(this.n * 31 + Math.floor(i / 6)) - 0.5) * vary;
      col[i * 3] = c.r * k;
      col[i * 3 + 1] = c.g * k;
      col[i * 3 + 2] = c.b * k;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    this.parts.push(geo);
    this.n++;
  }

  box(w: number, h: number, d: number, x: number, y: number, z: number, color: number, vary = 0.08, ry = 0): void {
    const g = new THREE.BoxGeometry(w, h, d);
    if (ry) g.rotateY(ry);
    this.push(g.translate(x, y, z), color, vary);
  }

  cyl(r: number, h: number, x: number, y: number, z: number, color: number, seg = 10, vary = 0.06): void {
    this.push(new THREE.CylinderGeometry(r, r, h, seg, 1).translate(x, y, z), color, vary);
  }

  build(): THREE.BufferGeometry {
    const g = mergeGeometries(this.parts, false);
    for (const p of this.parts) p.dispose();
    this.parts.length = 0;
    return g;
  }
}

const BULB = new THREE.Vector3(-0.42, 2.22, -0.6);
const W = 2.45; // half width
const BACK = -1.95;
const FRONT = 2.5;
const H = 2.65;
/** The room's box (the baked shell in art/models/blender/interior_bake.py mirrors it; interiorShell.test.ts checks). */
export const BACK_ROOM_BOX = { W, BACK, FRONT, H, wall: 0.05 };
const SHELL_MATS = {
  lino: { tile: 'lino', size: 0.6, mean: 0x6a5238, color: 0xffffff, roughness: 0.6 },
  paperB: { tile: 'paperB', size: 0.8, mean: 0x9a8a62, color: 0xffffff, roughness: 0.95 },
  distemper: { tile: 'distemper', size: 0.9, mean: 0xe2ddd6, color: 0x3a4a38, roughness: 0.9 },
  ceiling: { color: 0x5b5242, roughness: 0.95 },
};
/** Lower than the hideout's: the room stays the backdrop. */
const LIGHTMAP_INTENSITY = 2.0;
/** Where the Meshy counter (its till at the left end: mirrored) and shelving stand. */
const COUNTER_AT = new THREE.Vector3(0, 0, -0.06);
const SHELVES_AT = new THREE.Vector3(1.5, 0, BACK + 0.36);
/** The Meshy pieces are kept darker than drawn (the busy shelving most): the room is the backdrop, the weapon the hero. */
const COUNTER_DIM = 0.75;
const SHELVES_DIM = 0.6;

function buildRoom(k: RoomKit): void {
  const brown = 0x3a2516;
  // The lino, the paper over the green dado and the ceiling are the baked shell; here the dado rail and the
  // skirting on the four walls, and the two joists.
  for (const [w, x, z, ry] of [
    [2 * W, 0, BACK, 0],
    [FRONT - BACK, -W, (FRONT + BACK) / 2, Math.PI / 2],
    [FRONT - BACK, W, (FRONT + BACK) / 2, Math.PI / 2],
    [2 * W, 0, FRONT, 0],
  ] as const) {
    k.box(w, 0.05, 0.14, x, 1.0, z, brown, 0.05, ry);
    k.box(w, 0.12, 0.13, x, 0.06, z, 0x241810, 0.05, ry); // skirting
  }
  for (const z of [-0.6, 1.0]) k.box(2 * W, 0.16, 0.12, 0, H - 0.08, z, 0x3b2a1a, 0.08);

  // Sid's empty stool (he's out the back) and the tea chest he used to sell off in the yard.
  k.cyl(0.17, 0.04, 0.85, 0.62, -0.95, 0x4a2f1c);
  for (const [dx, dz] of [[-0.11, -0.11], [0.11, -0.11], [-0.11, 0.11], [0.11, 0.11]] as const) k.cyl(0.018, 0.6, 0.85 + dx, 0.3, -0.95 + dz, 0x3a2416, 6);
  k.box(0.62, 0.62, 0.5, 1.95, 0.31, -0.85, 0x6a5638, 0.12, 0.2);
  k.box(0.64, 0.03, 0.52, 1.95, 0.63, -0.85, 0x4d3f2a, 0.1, 0.2);
  k.box(0.55, 0.4, 0.45, -2.0, 0.2, -1.4, 0x5a4a32, 0.14, -0.15);
  k.box(0.45, 0.3, 0.36, -1.98, 0.55, -1.42, 0x6b5a3c, 0.14, 0.1);

  // The rack: two battens with pairs of pegs, on the left of the back wall (empty: what's for sale is out on show).
  for (const y of [2.0, 1.64, 1.3]) {
    k.box(1.0, 0.05, 0.03, -1.3, y - 0.07, BACK + 0.07, 0x4a2f1c, 0.05);
    for (const px of [-1.62, -0.98]) k.box(0.03, 0.03, 0.14, px, y - 0.05, BACK + 0.13, 0x2a1d12, 0.04);
  }
  // A coat on a hook by the rack, and the electric meter box.
  k.box(0.36, 0.8, 0.12, -2.2, 1.55, BACK + 0.12, 0x2f2a24, 0.08);
  k.box(0.32, 0.4, 0.12, 2.32, 2.05, 0.9, 0x2a2a28, 0.06, Math.PI / 2);
  // The bulb's flex and lampholder.
  k.box(0.012, H - BULB.y - 0.08, 0.012, BULB.x, (H + BULB.y + 0.08) / 2, BULB.z, 0x111111, 0);
  k.cyl(0.022, 0.06, BULB.x, BULB.y + 0.08, BULB.z, 0x2a1f16, 8, 0);
}

/** The counter: varnished deal, panelled front, a thick top (the Meshy counter stands in for it on MEDIUM and HIGH). */
function buildCounter(k: RoomKit): void {
  k.box(2.9, 0.92, 0.66, 0, 0.46, -0.08, 0x3a2416, 0.06);
  for (let i = 0; i < 6; i++) k.box(0.04, 0.78, 0.03, -1.2 + i * 0.48, 0.47, 0.26, 0x4a2f1c, 0.05);
  k.box(2.9, 0.05, 0.03, 0, 0.86, 0.26, 0x4a2f1c, 0.05);
  k.box(3.02, 0.055, 0.78, 0, 0.947, -0.08, 0x5a3a22, 0.05);
}

/** Shelves on the back wall: three planks on brackets, stocked with boxed rounds, tins and jars (or the Meshy shelving). */
function buildShelves(k: RoomKit): void {
  const SX = 1.45;
  const SZ = BACK + 0.2;
  for (const y of [1.15, 1.55, 1.95]) {
    k.box(1.3, 0.035, 0.3, SX, y, SZ, 0x4d3420, 0.06);
    for (const bx of [SX - 0.55, SX + 0.55]) k.box(0.03, 0.14, 0.24, bx, y - 0.085, SZ, 0x2a1d12, 0.04);
  }
  let seed = 1;
  const rnd = (): number => hash(seed++ * 7.13);
  for (const y of [1.15, 1.55, 1.95]) {
    let x = SX - 0.6;
    while (x < SX + 0.55) {
      const r = rnd();
      if (r < 0.45) {
        // A stack of card boxes of rounds: buff with a red band.
        const n = 1 + Math.floor(rnd() * 3);
        for (let j = 0; j < n; j++) {
          k.box(0.13, 0.07, 0.09, x + 0.065, y + 0.053 + j * 0.072, SZ + 0.02, j % 2 ? 0x9c7a4c : 0xa88552, 0.1, (rnd() - 0.5) * 0.3);
          k.box(0.132, 0.022, 0.092, x + 0.065, y + 0.053 + j * 0.072, SZ + 0.02, 0x8e2a1e, 0.06);
        }
        x += 0.16;
      } else if (r < 0.75) {
        // A tin (cleaning oil, dubbin, lead shot).
        const tall = 0.08 + rnd() * 0.08;
        k.cyl(0.042, tall, x + 0.045, y + 0.018 + tall / 2, SZ, [0x5a6a3a, 0x7a2a1e, 0x8a8a7a][Math.floor(rnd() * 3)]!, 10);
        x += 0.11;
      } else {
        // A brown jar.
        k.cyl(0.05, 0.14, x + 0.05, y + 0.088, SZ, 0x4a2a12, 10, 0.04);
        k.cyl(0.052, 0.025, x + 0.05, y + 0.17, SZ, 0x2a2a2a, 10, 0.02);
        x += 0.13;
      }
    }
  }
  // An old valve wireless on the top shelf, end of the row.
  k.box(0.3, 0.2, 0.17, SX + 0.42, 2.07, SZ + 0.02, 0x5a3a22, 0.05);
  k.box(0.2, 0.12, 0.01, SX + 0.4, 2.07, SZ + 0.11, 0xb8a47a, 0.05);
}

/** A hand-lettered card pinned to the wall. */
function signTexture(): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 160;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#d9cba4';
  g.fillRect(0, 0, 256, 160);
  g.strokeStyle = '#3a2a1a';
  g.lineWidth = 6;
  g.strokeRect(6, 6, 244, 148);
  g.fillStyle = '#8c1d11';
  g.textAlign = 'center';
  g.font = "bold 58px 'BH Anton', Impact, 'Arial Narrow', sans-serif";
  g.fillText('CASH', 128, 66);
  g.fillText('ONLY', 128, 122);
  g.fillStyle = '#2a2018';
  g.font = "18px 'BH Elite', 'Courier New', monospace";
  g.fillText('no tick · no names', 128, 146);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A soft round spot (white centre to clear edge): the light pool on the baize and each speck of dust. */
function glowTexture(size: number): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  if (!g) return null;
  const r = size / 2;
  const grad = g.createRadialGradient(r, r, 0, r, r, r);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(c);
}

/** Dust in the beam: a slow drift up through a box around the showcase, wrapping round. */
const MOTES = 70;
const MOTE_BOX = new THREE.Vector3(1.7, 1.2, 0.9);

export interface Stage {
  /** The part of the canvas the showcase is framed into (CSS px): what the panel and the strip leave free. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export class BackRoomScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(40, 1, 0.05, 20);
  readonly items: Item[] = [];
  private readonly disposables: { dispose(): void }[] = [];
  private readonly key: THREE.SpotLight;
  private readonly keyBase: number;
  private readonly pool: THREE.Mesh | null = null;
  private readonly motes: THREE.Points | null = null;
  private readonly moteSeed: Float32Array = new Float32Array(MOTES * 4);
  private selected = -1;
  private time = 0;
  /** Seconds since the weapon in the showcase arrived (its turn starts from the three-quarter view). */
  private shown = 0;
  /** Seconds into the "into your coat" drop, or -1. */
  private pocketT = -1;
  /** The procedural counter and shelves when they are their own mesh (MEDIUM and HIGH, until the Meshy ones arrive). */
  private furniture: THREE.Mesh | null = null;
  private readonly felt: THREE.Mesh;
  /** The baked floor, walls and ceiling (tests, the debug state: `shell.loaded`). */
  readonly shell: InteriorShell;
  private readonly shadows: boolean;
  private pack = false;
  private disposed = false;
  /** The Meshy counter and shelving are up (tests, the smoke spec). */
  meshy = false;

  /**
   * @param weapons what is for sale, in any order (browsing order is ROOM_ORDER).
   * @param renderer the game's renderer, for the room's reflection map (null in tests: no reflections).
   * @param detailed the Meshy counter and shelving (`backroom` pack: MEDIUM and HIGH); LOW keeps the procedural ones.
   */
  constructor(weapons: readonly WeaponId[], renderer: THREE.WebGLRenderer | null, shadows: boolean, detailed = shadows) {
    const sc = this.scene;
    sc.name = 'back-room';
    sc.background = new THREE.Color(0x0a0807);
    // The room falls away into the dark behind the showcase: a near-black fog from just past the weapon.
    sc.fog = new THREE.Fog(0x0a0807, 2.1, 5.2);
    acquireWeaponMaterials();

    this.shadows = shadows;
    // The shell: lino, paper, dado and ceiling with the baked bounce and occlusion; then the trim and furniture.
    this.shell = buildInteriorShell(backroomLayout as ShellLayout, { specs: SHELL_MATS, lightMapIntensity: LIGHTMAP_INTENSITY, load: typeof document !== 'undefined' });
    sc.add(this.shell.mesh);
    this.disposables.push(this.shell);
    const kit = new RoomKit();
    buildRoom(kit);
    const roomMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88 });
    if (detailed) {
      // The counter and the shelves are their own mesh, taken down when the Meshy pieces come in.
      const fk = new RoomKit();
      buildCounter(fk);
      buildShelves(fk);
      const fGeo = fk.build();
      this.furniture = new THREE.Mesh(fGeo, roomMat);
      this.furniture.receiveShadow = true;
      sc.add(this.furniture);
      this.disposables.push(fGeo);
      void loadModelPack('backroom').then((pack) => {
        if (!pack) return;
        if (this.disposed) {
          releaseModelPack('backroom');
          return;
        }
        this.pack = true;
        this.furnish(pack.get('shop-counter'), pack.get('shop-shelves'));
      });
    } else {
      buildCounter(kit);
      buildShelves(kit);
    }
    const roomGeo = kit.build();
    const room = new THREE.Mesh(roomGeo, roomMat);
    room.receiveShadow = true;
    sc.add(room);
    this.disposables.push(roomGeo, roomMat);

    // Green baize on the counter top (its own material: soft, no sheen).
    const feltGeo = new THREE.BoxGeometry(2.25, 0.01, 0.58);
    const feltMat = new THREE.MeshStandardMaterial({ color: 0x203d28, roughness: 1 });
    const felt = new THREE.Mesh(feltGeo, feltMat);
    felt.position.set(-0.25, FELT_TOP - 0.005, -0.06);
    felt.receiveShadow = true;
    sc.add(felt);
    this.felt = felt;
    this.disposables.push(feltGeo, feltMat);

    // The bare bulb at the back (a glow in the haze) and its weak light: the room is only the backdrop now.
    const bulbGeo = new THREE.SphereGeometry(0.045, 12, 8);
    const bulbMat = new THREE.MeshStandardMaterial({ color: 0x000000, emissive: 0xffd9a0, emissiveIntensity: 5, fog: false });
    const bulb = new THREE.Mesh(bulbGeo, bulbMat);
    bulb.position.copy(BULB);
    sc.add(bulb);
    this.disposables.push(bulbGeo, bulbMat);
    const roomLight = new THREE.PointLight(0xffc488, 2.6, 0, 1.6);
    roomLight.position.copy(BULB);
    roomLight.position.y -= 0.06;
    sc.add(roomLight);
    const hemi = new THREE.HemisphereLight(0x7a6c56, 0x140e0a, 0.28);
    sc.add(hemi);
    this.disposables.push(roomLight, hemi);

    // The showcase: a warm key from high front-left (the only shadow), a cool rim from behind-right that
    // draws the weapon's edge against the dark, and a weak fill from the eye so blued steel keeps its shape.
    this.key = new THREE.SpotLight(0xffd9a8, 6, 0, 0.36, 0.75, 1.6);
    this.keyBase = this.key.intensity;
    this.key.position.set(SHOW.x - 0.75, SHOW.y + 1.25, SHOW.z + 0.85);
    this.key.target.position.copy(SHOW).setY(FELT_TOP + 0.15);
    if (shadows) {
      this.key.castShadow = true;
      this.key.shadow.mapSize.set(1024, 1024);
      this.key.shadow.bias = -0.0008;
      this.key.shadow.normalBias = 0.01;
      this.key.shadow.camera.near = 0.3;
      this.key.shadow.camera.far = 4;
      this.key.shadow.radius = 4;
    }
    sc.add(this.key, this.key.target);
    const rim = new THREE.SpotLight(0x9db8ff, 10, 0, 0.3, 0.6, 1.6);
    rim.position.set(SHOW.x + 0.9, SHOW.y + 0.75, SHOW.z - 1.05);
    rim.target.position.copy(SHOW);
    sc.add(rim, rim.target);
    const rim2 = new THREE.SpotLight(0xffb070, 7, 0, 0.3, 0.6, 1.6);
    rim2.position.set(SHOW.x - 1.0, SHOW.y + 0.4, SHOW.z - 0.9);
    rim2.target.position.copy(SHOW);
    sc.add(rim2, rim2.target);
    const fill = new THREE.DirectionalLight(0xc8b8a0, 0.5);
    fill.position.copy(EYE).add(_v.set(0.3, 0.4, 0));
    fill.target.position.copy(SHOW);
    sc.add(fill, fill.target);
    this.disposables.push(this.key, rim, rim2, fill);

    // The pool of the key light on the baize under the weapon.
    const glow = glowTexture(128);
    if (glow) {
      const poolGeo = new THREE.PlaneGeometry(1.5, 0.62).rotateX(-Math.PI / 2);
      const poolMat = new THREE.MeshBasicMaterial({ map: glow, color: 0xffc27a, transparent: true, opacity: 0.12, blending: THREE.AdditiveBlending, depthWrite: false });
      this.pool = new THREE.Mesh(poolGeo, poolMat);
      this.pool.position.set(SHOW.x, FELT_TOP + 0.003, SHOW.z - 0.04);
      sc.add(this.pool);
      this.disposables.push(poolGeo, poolMat, glow);

      // Dust motes drifting in the beam.
      const pos = new Float32Array(MOTES * 3);
      for (let i = 0; i < MOTES; i++) {
        this.moteSeed[i * 4] = hash(i * 3.1) - 0.5;
        this.moteSeed[i * 4 + 1] = hash(i * 5.7);
        this.moteSeed[i * 4 + 2] = hash(i * 9.3) - 0.5;
        this.moteSeed[i * 4 + 3] = 0.03 + hash(i * 1.9) * 0.05;
      }
      const moteGeo = new THREE.BufferGeometry();
      moteGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const dot = glowTexture(32)!;
      const moteMat = new THREE.PointsMaterial({ map: dot, color: 0xffe2b0, size: 0.016, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
      this.motes = new THREE.Points(moteGeo, moteMat);
      this.motes.frustumCulled = false;
      sc.add(this.motes);
      this.disposables.push(moteGeo, moteMat, dot);
    }

    const tex = signTexture();
    if (tex) {
      const signGeo = new THREE.PlaneGeometry(0.42, 0.26);
      const signMat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95 });
      const sign = new THREE.Mesh(signGeo, signMat);
      sign.position.set(0.4, 1.66, -1.89);
      sign.rotation.z = 0.04;
      sc.add(sign);
      this.disposables.push(signGeo, signMat, tex);
    }

    if (renderer) {
      // Something for nickel and blued steel to reflect: a dim grey room (RoomEnvironment), built once per visit.
      const pmrem = new THREE.PMREMGenerator(renderer);
      const env = new RoomEnvironment();
      const rt = pmrem.fromScene(env, 0.04);
      env.dispose();
      pmrem.dispose();
      sc.environment = rt.texture;
      sc.environmentIntensity = 0.35;
      this.disposables.push(rt);
    }

    // Boxes of rounds at the far end of the counter.
    for (const [x, z, ry] of [[0.92, -0.12, 0.3], [0.96, -0.12, 0.25], [1.16, -0.26, -0.4]] as const) {
      const box = buildAmmoBox();
      box.scale.setScalar(1.25);
      box.position.set(x, FELT_TOP + 0.038 * 1.25 + (x === 0.96 ? 0.075 : 0), z);
      box.rotation.y = ry;
      sc.add(box);
    }

    // The weapons: each centred on its pivot in the showcase pose, scaled so the longest is HERO_MAX long.
    const basis = new THREE.Matrix4();
    const sizes: number[] = [];
    const meshes: THREE.Group[] = [];
    for (const id of ROOM_ORDER) {
      if (!weapons.includes(id)) continue;
      const mesh = buildWeaponMesh(id);
      mesh.scale.setScalar(1);
      const [ax, ay, az] = BASIS[SIDE[id] ?? 'x'];
      basis.makeBasis(ax, ay, az);
      mesh.quaternion.setFromRotationMatrix(basis);
      mesh.updateMatrixWorld(true);
      _box.setFromObject(mesh);
      sizes.push(_box.getSize(_v).x);
      meshes.push(mesh);
    }
    const longest = Math.max(0.01, ...sizes);
    meshes.forEach((mesh, i) => {
      const id = mesh.name.replace('weapon-', '') as WeaponId;
      const len = HERO_MIN + (HERO_MAX - HERO_MIN) * Math.sqrt(sizes[i]! / longest);
      mesh.scale.setScalar(len / Math.max(0.01, sizes[i]!));
      mesh.position.set(0, 0, 0);
      mesh.updateMatrixWorld(true);
      _box.setFromObject(mesh);
      mesh.position.sub(_box.getCenter(_v));
      const pivot = new THREE.Group();
      pivot.name = `room-${id}`;
      pivot.add(mesh);
      pivot.position.copy(SHOW);
      pivot.visible = false;
      mesh.traverse((o) => {
        if (o instanceof THREE.Mesh) o.castShadow = shadows;
      });
      sc.add(pivot);
      this.items.push({ id, pivot, mesh, x: SLIDE, wantX: SLIDE });
    });
    this.camera.position.copy(EYE);
    this.camera.lookAt(SHOW);
  }

  /** Put the Meshy counter and shelving in for the procedural ones (both, or neither). */
  private furnish(counter: PackModel | null, shelves: PackModel | null): void {
    if (!counter || !shelves || !this.furniture) return;
    this.scene.remove(this.furniture);
    this.furniture = null;
    for (const [model, at, dim, mirror] of [[counter, COUNTER_AT, COUNTER_DIM, true], [shelves, SHELVES_AT, SHELVES_DIM, false]] as const) {
      const m = interiorModel(model);
      m.material.color.setScalar(dim);
      m.mesh.position.copy(at);
      // the till stands at the left end, clear of the showcase and the ammo boxes
      if (mirror) m.mesh.scale.x = -1;
      m.mesh.receiveShadow = true;
      m.mesh.castShadow = this.shadows && model === counter;
      this.scene.add(m.mesh);
      this.disposables.push(m.material);
    }
    // The baize stops short of the till.
    this.felt.scale.x = 1.95 / 2.25;
    this.felt.position.x = -0.07;
    this.meshy = true;
  }

  /** Index (into `items`) of the weapon in the showcase, or -1. */
  get selectedIndex(): number {
    return this.selected;
  }

  /**
   * Put a weapon in the showcase. `dir` is the way you browsed (+1 next: the new one comes in from the
   * right and the old one leaves to the left; 0 = no slide, it is simply there).
   */
  select(i: number, dir = 1): void {
    const next = i >= 0 && i < this.items.length ? i : -1;
    if (next === this.selected) return;
    const old = this.items[this.selected];
    if (old) old.wantX = dir >= 0 ? -SLIDE : SLIDE;
    this.selected = next;
    const it = this.items[next];
    if (it) {
      it.x = dir === 0 ? 0 : dir > 0 ? SLIDE : -SLIDE;
      it.wantX = 0;
      this.shown = 0;
    }
    this.pocketT = -1;
  }

  /** The weapon was bought: it drops out of frame into your coat and comes back up. */
  pocket(): void {
    this.pocketT = 0;
  }

  /** Where the showcase centre is on screen (0..1 of the canvas). */
  screenPos(out: { x: number; y: number }): boolean {
    _v.copy(SHOW).project(this.camera);
    out.x = (_v.x + 1) / 2;
    out.y = (1 - _v.y) / 2;
    return _v.z < 1;
  }

  /**
   * Frame the showcase into the stage (the part of the canvas the panel and the strip leave free): the
   * projection centre moves to the stage's centre (a view offset) and the field of view opens until the
   * weapon and its swing fit both ways.
   */
  setView(width: number, height: number, stage: Stage): void {
    const w = Math.max(1, width);
    const h = Math.max(1, height);
    const aspect = w / h;
    const fw = Math.max(0.25, stage.w / w);
    const fh = Math.max(0.25, stage.h / h);
    const dist = EYE.distanceTo(SHOW);
    const tanV = Math.max(FIT_HALF_H / dist / fh, FIT_HALF_W / dist / (fw * aspect));
    this.camera.aspect = aspect;
    this.camera.setViewOffset(w, h, w / 2 - (stage.x + stage.w / 2), h / 2 - (stage.y + stage.h / 2), w, h);
    this.camera.fov = THREE.MathUtils.clamp(THREE.MathUtils.radToDeg(2 * Math.atan(tanV)), 18, 80);
    this.camera.updateProjectionMatrix();
  }

  update(dt: number): void {
    this.time += dt;
    this.shown += dt;
    const k = 1 - Math.exp(-dt * 8);
    // The pocket drop: a little lift, down out of frame, a beat, back up.
    let drop = 0;
    let lift = 0;
    if (this.pocketT >= 0) {
      this.pocketT += dt;
      const t = this.pocketT;
      lift = Math.sin(Math.min(1, t / 0.14) * Math.PI) * 0.05;
      drop = t < 0.14 ? 0 : t < 0.5 ? smooth((t - 0.14) / 0.36) : t < 0.8 ? 1 : 1 - smooth((t - 0.8) / (POCKET_TIME - 0.8));
      if (t >= POCKET_TIME) this.pocketT = -1;
    }
    // The key light flares as the weapon is bought.
    this.key.intensity = this.keyBase * (1 + (this.pocketT >= 0 && this.pocketT < 0.3 ? (1 - this.pocketT / 0.3) * 1.2 : 0));

    for (let i = 0; i < this.items.length; i++) {
      const it = this.items[i]!;
      it.x += (it.wantX - it.x) * k;
      if (Math.abs(it.x - it.wantX) < 0.002) it.x = it.wantX;
      const on = i === this.selected;
      it.pivot.visible = Math.abs(it.x) < SLIDE - 0.01;
      if (!it.pivot.visible) continue;
      // A slow turntable sway from the three-quarter view; sliding in, it carries a little extra turn.
      const sway = on ? Math.sin(this.shown * 0.42) * SWING : 0;
      _q.setFromAxisAngle(X_AXIS, 0.12 + Math.sin(this.time * 0.6) * 0.03);
      _q2.setFromAxisAngle(Y_AXIS, BASE_YAW + sway - it.x * 0.7);
      it.pivot.quaternion.copy(_q2).premultiply(_q);
      it.pivot.position.set(SHOW.x + it.x, SHOW.y + Math.sin(this.time * 1.3) * 0.012, SHOW.z - Math.abs(it.x) * 0.25);
      if (on) it.pivot.position.y += lift - drop * 0.95;
      it.pivot.scale.setScalar(on ? 1 + lift : 1);
    }
    if (this.pool) {
      const m = this.pool.material as THREE.MeshBasicMaterial;
      m.opacity = 0.12 * (1 - 0.6 * (this.pocketT >= 0 ? Math.min(1, this.pocketT * 3) * (this.pocketT < 0.8 ? 1 : 1 - smooth((this.pocketT - 0.8) / 0.5)) : 0));
    }
    if (this.motes) {
      const p = this.motes.geometry.getAttribute('position') as THREE.BufferAttribute;
      const a = p.array as Float32Array;
      for (let i = 0; i < MOTES; i++) {
        const s = this.moteSeed;
        const up = (s[i * 4 + 1]! + this.time * s[i * 4 + 3]!) % 1;
        a[i * 3] = SHOW.x + s[i * 4]! * MOTE_BOX.x + Math.sin(this.time * 0.3 + i) * 0.04;
        a[i * 3 + 1] = SHOW.y - MOTE_BOX.y / 2 + up * MOTE_BOX.y;
        a[i * 3 + 2] = SHOW.z + s[i * 4 + 2]! * MOTE_BOX.z + Math.cos(this.time * 0.23 + i * 1.7) * 0.04;
      }
      p.needsUpdate = true;
    }
    this.camera.position.copy(EYE);
    // The eye breathes a little, so the room's parallax shows it is a place.
    this.camera.position.x += Math.sin(this.time * 0.21) * 0.035;
    this.camera.position.y += Math.sin(this.time * 0.17) * 0.015;
    this.camera.lookAt(SHOW);
  }

  dispose(): void {
    this.disposed = true;
    if (this.pack) releaseModelPack('backroom');
    this.pack = false;
    for (const it of this.items) disposeWeaponMesh(it.mesh);
    this.items.length = 0;
    // The ammo boxes share the weapon kit's cached geometry and materials: they go with the scene.
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    this.scene.clear();
    this.scene.environment = null;
    releaseWeaponMaterials();
  }
}
